/**
 * Player progress — port of `EpisodeProgress` (EpisodeModels.swift) and of the
 * Supabase row mapping in ProgressRepository.swift. The JSON written to
 * `player_episode_progress.state` MUST keep the exact camelCase keys below so
 * the iOS app can read a row written by the web and vice versa.
 */
import type { ChatMessageContent, EpisodeContent, EpisodeRequest } from './episode';

export type RejectedProofReplyKind = 'close' | 'wrong' | 'textAnswerReminder';

export interface RejectedProofReplyReference {
  kind: RejectedProofReplyKind;
  variantIndex?: number | null;
}

export interface RejectedProofAttempt {
  id: string;
  requestID: string;
  itemID: string;
  title: string;
  response: string;
  replyReference?: RejectedProofReplyReference | null;
}

export interface EpisodeProgress {
  currentRequestIndex: number;
  completedRequestIDs: Set<string>;
  sentProofIDs: Set<string>;
  hintTokensRemaining: number;
  hintsRevealedByRequestID: Record<string, number>;
  submittedProofTitlesByRequestID: Record<string, string>;
  submittedProofItemIDsByRequestID?: Record<string, string>;
  stagedProofDeliveryRequestIDs?: Set<string>;
  submittedAnswersByRequestID: Record<string, string>;
  rejectedProofAttemptsByRequestID?: Record<string, RejectedProofAttempt[]>;
  playerFirstName?: string;
  onboardingReplyText?: string;
  copiedPhoneUnlockedAtLeastOnce?: boolean;
  /** Local only, never persisted remotely. */
  unlockedNoteGroups?: Set<string>;
  /** Local only, never persisted remotely: requests whose prompt was seen (one-shot `request_viewed`). */
  viewedRequestIDs?: Set<string>;
  premiumGateReached: boolean;
  /** Derived from premium_entitlements at load time; never trusted from a save. */
  isPremium: boolean;
  solved: boolean;
  xpEarned: number;
  badgeProgressText?: string;
  dialogueRepliesByMessageID?: Record<string, string>;
  dialogueReplyOptionIndicesByMessageID?: Record<string, number>;
  deliveredChatMessageIDs?: Set<string>;
  conclusionUnlocked?: boolean;
}

export const FREE_HINT_TOKENS = 3;

export function newProgress(): EpisodeProgress {
  return {
    currentRequestIndex: 0,
    completedRequestIDs: new Set(),
    sentProofIDs: new Set(),
    hintTokensRemaining: FREE_HINT_TOKENS,
    hintsRevealedByRequestID: {},
    submittedProofTitlesByRequestID: {},
    submittedAnswersByRequestID: {},
    premiumGateReached: false,
    isPremium: false,
    solved: false,
    xpEarned: 0,
  };
}

export function cloneProgress(p: EpisodeProgress): EpisodeProgress {
  return {
    ...p,
    completedRequestIDs: new Set(p.completedRequestIDs),
    sentProofIDs: new Set(p.sentProofIDs),
    stagedProofDeliveryRequestIDs: p.stagedProofDeliveryRequestIDs ? new Set(p.stagedProofDeliveryRequestIDs) : undefined,
    unlockedNoteGroups: p.unlockedNoteGroups ? new Set(p.unlockedNoteGroups) : undefined,
    viewedRequestIDs: p.viewedRequestIDs ? new Set(p.viewedRequestIDs) : undefined,
    deliveredChatMessageIDs: p.deliveredChatMessageIDs ? new Set(p.deliveredChatMessageIDs) : undefined,
    hintsRevealedByRequestID: { ...p.hintsRevealedByRequestID },
    submittedProofTitlesByRequestID: { ...p.submittedProofTitlesByRequestID },
    submittedProofItemIDsByRequestID: p.submittedProofItemIDsByRequestID ? { ...p.submittedProofItemIDsByRequestID } : undefined,
    submittedAnswersByRequestID: { ...p.submittedAnswersByRequestID },
    rejectedProofAttemptsByRequestID: p.rejectedProofAttemptsByRequestID
      ? Object.fromEntries(Object.entries(p.rejectedProofAttemptsByRequestID).map(([k, v]) => [k, v.map((a) => ({ ...a }))]))
      : undefined,
    dialogueRepliesByMessageID: p.dialogueRepliesByMessageID ? { ...p.dialogueRepliesByMessageID } : undefined,
    dialogueReplyOptionIndicesByMessageID: p.dialogueReplyOptionIndicesByMessageID ? { ...p.dialogueReplyOptionIndicesByMessageID } : undefined,
  };
}

// ---------- queries (pure) ----------

export function isBlockedByPremium(p: EpisodeProgress): boolean {
  return p.premiumGateReached && !p.isPremium;
}

export function currentRequest(p: EpisodeProgress, episode: EpisodeContent): EpisodeRequest | undefined {
  return episode.requests[p.currentRequestIndex];
}

export function revealedHintCount(p: EpisodeProgress, request: EpisodeRequest): number {
  return Math.min(p.hintsRevealedByRequestID[request.id] ?? 0, request.hints.length);
}

export function submittedChoiceID(p: EpisodeProgress, request: EpisodeRequest): string | undefined {
  if (request.answerType !== 'choice') return undefined;
  const answer = p.submittedAnswersByRequestID[request.id];
  if (answer == null) return undefined;
  return request.choices?.find((c) => c.text === answer)?.id;
}

/** Every choice id picked so far, in request order. */
export function submittedChoiceIDs(p: EpisodeProgress, episode: EpisodeContent): string[] {
  return episode.requests.map((r) => submittedChoiceID(p, r)).filter((id): id is string => id != null);
}

export function rejectedProofAttempts(p: EpisodeProgress, request: EpisodeRequest): RejectedProofAttempt[] {
  return p.rejectedProofAttemptsByRequestID?.[request.id] ?? [];
}

export function isNoteGroupUnlocked(p: EpisodeProgress, group: string): boolean {
  return p.unlockedNoteGroups?.has(group) ?? false;
}

export function resolvedDialogueReply(p: EpisodeProgress, message: ChatMessageContent): string | undefined {
  const stored = p.dialogueRepliesByMessageID?.[message.id];
  if (stored == null) return undefined;
  const index = p.dialogueReplyOptionIndicesByMessageID?.[message.id] ?? message.replyOptions?.indexOf(stored);
  if (index != null && index >= 0 && message.replyOptions && index < message.replyOptions.length) {
    return message.replyOptions[index];
  }
  return stored;
}

// ---------- mutations (return a new object) ----------

export function completeCurrentRequest(p: EpisodeProgress, episode: EpisodeContent): { progress: EpisodeProgress; request?: EpisodeRequest } {
  const request = currentRequest(p, episode);
  if (!request) return { progress: p };
  const next = cloneProgress(p);
  next.completedRequestIDs.add(request.id);
  if (next.currentRequestIndex < episode.requests.length - 1) {
    next.currentRequestIndex += 1;
  } else {
    next.solved = true;
    next.xpEarned = Math.max(next.xpEarned, episode.reward.xp);
    next.badgeProgressText = episode.reward.badgeProgressText;
  }
  if (request.id === episode.paywallAfterRequestID) next.premiumGateReached = true;
  return { progress: next, request };
}

/** Rebuilds the cursor from stable request ids (never trust a stored index). */
export function reconcileCurrentRequest(p: EpisodeProgress, episode: EpisodeContent): EpisodeProgress {
  const next = cloneProgress(p);
  if (episode.requests.length === 0) { next.currentRequestIndex = 0; return next; }
  if (next.solved) { next.currentRequestIndex = Math.min(Math.max(next.currentRequestIndex, 0), episode.requests.length - 1); return next; }
  const firstIncomplete = episode.requests.findIndex((r) => !next.completedRequestIDs.has(r.id));
  next.currentRequestIndex = firstIncomplete >= 0 ? firstIncomplete : episode.requests.length - 1;
  return next;
}

export function revealNextHint(p: EpisodeProgress, request: EpisodeRequest): { progress: EpisodeProgress; hint?: string } {
  const count = revealedHintCount(p, request);
  if (count >= request.hints.length) return { progress: p };
  const next = cloneProgress(p);
  if (!next.isPremium) {
    if (next.hintTokensRemaining <= 0) return { progress: p };
    next.hintTokensRemaining -= 1;
  }
  next.hintsRevealedByRequestID[request.id] = count + 1;
  return { progress: next, hint: request.hints[count] };
}

export function recordDialogueReply(p: EpisodeProgress, reply: string, message: ChatMessageContent): EpisodeProgress {
  const index = message.replyOptions?.indexOf(reply) ?? -1;
  if (index < 0) return p;
  const next = cloneProgress(p);
  next.dialogueRepliesByMessageID = { ...next.dialogueRepliesByMessageID, [message.id]: reply };
  next.dialogueReplyOptionIndicesByMessageID = { ...next.dialogueReplyOptionIndicesByMessageID, [message.id]: index };
  return next;
}

export function unlockNoteGroup(p: EpisodeProgress, group: string): EpisodeProgress {
  const next = cloneProgress(p);
  next.unlockedNoteGroups = new Set(next.unlockedNoteGroups ?? []);
  next.unlockedNoteGroups.add(group);
  return next;
}

/**
 * EpisodeProgress.markRequestViewed: returns the progress with `requestID` marked as viewed,
 * or `undefined` when it already was (the caller tracks `request_viewed` only then).
 */
export function markRequestViewed(p: EpisodeProgress, requestID: string): EpisodeProgress | undefined {
  if (p.viewedRequestIDs?.has(requestID)) return undefined;
  const next = cloneProgress(p);
  next.viewedRequestIDs = new Set(next.viewedRequestIDs ?? []);
  next.viewedRequestIDs.add(requestID);
  return next;
}

/**
 * Local-only fields survive a reload of the same episode (language change, retry): they are
 * never in the Supabase row, so `progressFromRow` cannot restore them.
 */
export function carryLocalOnlyProgress(previous: EpisodeProgress | undefined, loaded: EpisodeProgress): EpisodeProgress {
  if (!previous || (!previous.viewedRequestIDs && !previous.unlockedNoteGroups)) return loaded;
  const next = cloneProgress(loaded);
  if (previous.viewedRequestIDs) next.viewedRequestIDs = new Set([...(loaded.viewedRequestIDs ?? []), ...previous.viewedRequestIDs]);
  if (previous.unlockedNoteGroups) next.unlockedNoteGroups = new Set([...(loaded.unlockedNoteGroups ?? []), ...previous.unlockedNoteGroups]);
  return next;
}

// ---------- Supabase row (de)serialization ----------

/** Exact shape of `player_episode_progress.state` (jsonb). Sets are JSON arrays. */
export interface ProgressStateJSON {
  submittedProofTitlesByRequestID: Record<string, string>;
  submittedProofItemIDsByRequestID: Record<string, string>;
  stagedProofDeliveryRequestIDs: string[];
  submittedAnswersByRequestID: Record<string, string>;
  rejectedProofAttemptsByRequestID: Record<string, RejectedProofAttempt[]>;
  playerFirstName?: string;
  onboardingReplyText?: string;
  copiedPhoneUnlockedAtLeastOnce?: boolean;
  dialogueRepliesByMessageID?: Record<string, string>;
  dialogueReplyOptionIndicesByMessageID?: Record<string, number>;
  deliveredChatMessageIDs?: string[];
  conclusionUnlocked?: boolean;
}

export interface ProgressRow {
  user_id: string;
  episode_id: string;
  current_request_id: string | null;
  completed_request_ids: string[];
  sent_proof_ids: string[];
  hint_tokens_remaining: number;
  hints_used: Record<string, number>;
  premium_gate_reached: boolean;
  solved: boolean;
  xp: number;
  badge: string | null;
  state: ProgressStateJSON | null;
}

function definedOnly<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

export function progressToState(p: EpisodeProgress): ProgressStateJSON {
  return definedOnly({
    submittedProofTitlesByRequestID: p.submittedProofTitlesByRequestID,
    submittedProofItemIDsByRequestID: p.submittedProofItemIDsByRequestID ?? {},
    stagedProofDeliveryRequestIDs: [...(p.stagedProofDeliveryRequestIDs ?? [])].sort(),
    submittedAnswersByRequestID: p.submittedAnswersByRequestID,
    rejectedProofAttemptsByRequestID: p.rejectedProofAttemptsByRequestID ?? {},
    playerFirstName: p.playerFirstName,
    onboardingReplyText: p.onboardingReplyText,
    copiedPhoneUnlockedAtLeastOnce: p.copiedPhoneUnlockedAtLeastOnce,
    dialogueRepliesByMessageID: p.dialogueRepliesByMessageID,
    dialogueReplyOptionIndicesByMessageID: p.dialogueReplyOptionIndicesByMessageID,
    deliveredChatMessageIDs: p.deliveredChatMessageIDs ? [...p.deliveredChatMessageIDs].sort() : undefined,
    conclusionUnlocked: p.conclusionUnlocked,
  });
}

export function progressToRow(userID: string, episode: EpisodeContent, p: EpisodeProgress): ProgressRow {
  return {
    user_id: userID,
    episode_id: episode.id,
    current_request_id: currentRequest(p, episode)?.id ?? null,
    completed_request_ids: [...p.completedRequestIDs].sort(),
    sent_proof_ids: [...p.sentProofIDs].sort(),
    hint_tokens_remaining: p.hintTokensRemaining,
    hints_used: p.hintsRevealedByRequestID,
    premium_gate_reached: p.premiumGateReached,
    solved: p.solved,
    xp: p.xpEarned,
    badge: p.badgeProgressText ?? null,
    state: progressToState(p),
  };
}

/** True when both progresses serialize to the same Supabase row (a local-only change needs no save). */
export function sameProgressRow(episode: EpisodeContent, a: EpisodeProgress, b: EpisodeProgress): boolean {
  return JSON.stringify(progressToRow('', episode, a)) === JSON.stringify(progressToRow('', episode, b));
}

export function progressFromRow(row: ProgressRow, episode: EpisodeContent): EpisodeProgress {
  const p = newProgress();
  p.completedRequestIDs = new Set(row.completed_request_ids ?? []);
  const idx = row.current_request_id ? episode.requests.findIndex((r) => r.id === row.current_request_id) : -1;
  if (idx >= 0) p.currentRequestIndex = idx;
  else {
    const next = episode.requests.findIndex((r) => !p.completedRequestIDs.has(r.id));
    p.currentRequestIndex = next >= 0 ? next : Math.max(0, episode.requests.length - 1);
  }
  p.sentProofIDs = new Set(row.sent_proof_ids ?? []);
  p.hintTokensRemaining = row.hint_tokens_remaining ?? FREE_HINT_TOKENS;
  p.hintsRevealedByRequestID = row.hints_used ?? {};
  p.premiumGateReached = !!row.premium_gate_reached;
  p.solved = !!row.solved;
  p.xpEarned = row.xp ?? 0;
  p.badgeProgressText = row.badge ?? undefined;
  const s = row.state;
  if (s) {
    p.submittedProofTitlesByRequestID = s.submittedProofTitlesByRequestID ?? {};
    p.submittedProofItemIDsByRequestID = s.submittedProofItemIDsByRequestID;
    p.stagedProofDeliveryRequestIDs = s.stagedProofDeliveryRequestIDs ? new Set(s.stagedProofDeliveryRequestIDs) : undefined;
    p.submittedAnswersByRequestID = s.submittedAnswersByRequestID ?? {};
    p.rejectedProofAttemptsByRequestID = s.rejectedProofAttemptsByRequestID;
    p.playerFirstName = s.playerFirstName;
    p.onboardingReplyText = s.onboardingReplyText;
    p.copiedPhoneUnlockedAtLeastOnce = s.copiedPhoneUnlockedAtLeastOnce;
    p.dialogueRepliesByMessageID = s.dialogueRepliesByMessageID;
    p.dialogueReplyOptionIndicesByMessageID = s.dialogueReplyOptionIndicesByMessageID;
    p.deliveredChatMessageIDs = s.deliveredChatMessageIDs ? new Set(s.deliveredChatMessageIDs) : undefined;
    p.conclusionUnlocked = s.conclusionUnlocked;
  }
  return reconcileCurrentRequest(p, episode);
}

/** Local cache (localStorage) uses the same row shape so a cached save and a remote row are interchangeable. */
export const LOCAL_PROGRESS_KEY = (episodeID: string) => `detectea.progress.${episodeID}`;

/** Port of AppRootView.shouldColdOpenClientChat: nothing done yet in this episode. */
export function isUntouchedProgress(p: EpisodeProgress): boolean {
  return p.currentRequestIndex === 0
    && p.completedRequestIDs.size === 0
    && Object.keys(p.submittedAnswersByRequestID).length === 0
    && Object.keys(p.submittedProofTitlesByRequestID).length === 0
    && Object.keys(p.rejectedProofAttemptsByRequestID ?? {}).length === 0
    && !p.solved
    && !isBlockedByPremium(p);
}
